/**
 * Cart Properties to Attributes Converter
 * 
 * Konvertiert alle Properties der Cart Items automatisch zu Cart Attributes
 * beim Klick auf den Checkout-Button.
 */

console.log('🛒 Cart Properties to Attributes Converter geladen');

class CartPropertiesConverter {
  constructor() {
    this.checkoutButton = null;
    this.originalCheckoutUrl = '/checkout';
    this.isProcessing = false;
    
    this.init();
  }

  init() {
    // Warten bis DOM geladen ist
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setupCheckoutInterceptor());
    } else {
      this.setupCheckoutInterceptor();
    }
  }

  setupCheckoutInterceptor() {
    console.log('🔧 Setting up checkout interceptor...');
    
    // Finde alle möglichen Checkout-Buttons (Cart Page + Cart Drawer + Dynamic Checkout)
    const checkoutButtons = document.querySelectorAll('button[name="checkout"], .cart__checkout-button, [data-checkout-button]');
    
    // Auch Dynamic Checkout Buttons abfangen (PayPal, Apple Pay, etc.)
    const dynamicCheckoutContainer = document.querySelector('.cart__dynamic-checkout-buttons, .additional-checkout-buttons');
    if (dynamicCheckoutContainer) {
      const dynamicButtons = dynamicCheckoutContainer.querySelectorAll('button, input[type="submit"], [role="button"]');
      dynamicButtons.forEach(button => {
        if (!button._propertiesConverterAttached) {
          button.addEventListener('click', (e) => {
            console.log('🎯 Dynamic Checkout Button geklickt, konvertiere Properties...');
            this.handleCheckoutClick(e);
          });
          button._propertiesConverterAttached = true;
        }
      });
      console.log(`✅ ${dynamicButtons.length} Dynamic Checkout Button(s) abgefangen`);
    }
    
    if (checkoutButtons.length === 0) {
      console.warn('⚠️ Keine Checkout-Buttons gefunden');
      return;
    }

    console.log(`✅ ${checkoutButtons.length} Checkout-Button(s) gefunden`);

    // Event Listener für alle Checkout-Buttons
    checkoutButtons.forEach((button, index) => {
      // Entferne vorherige Event Listener falls vorhanden
      if (button._propertiesConverterAttached) {
        return;
      }
      
      button.addEventListener('click', (e) => {
        this.handleCheckoutClick(e);
      });
      
      button._propertiesConverterAttached = true;
      console.log(`✅ Event Listener für Button ${index + 1} hinzugefügt`);
    });

    // Auch für Form Submit abfangen (falls Button in Form ist)
    const cartForms = document.querySelectorAll('#cart, [data-cart-form]');
    cartForms.forEach((form, index) => {
      if (form._propertiesConverterAttached) {
        return;
      }
      
      form.addEventListener('submit', (e) => {
        // Nur abfangen wenn es ein Checkout-Submit ist
        if (e.submitter && (e.submitter.name === 'checkout' || e.submitter.classList.contains('cart__checkout-button'))) {
          this.handleCheckoutClick(e);
        }
      });
      
      form._propertiesConverterAttached = true;
      console.log(`✅ Form Submit Listener für Form ${index + 1} hinzugefügt`);
    });

    console.log('✅ Checkout-Interceptor erfolgreich eingerichtet');
  }

  async handleCheckoutClick(event) {
    if (this.isProcessing) {
      console.log('⏳ Bereits in Bearbeitung, ignoriere Klick');
      return;
    }

    console.log('🚀 Checkout-Klick abgefangen, starte Properties-Konvertierung...');
    
    // Event stoppen
    event.preventDefault();
    event.stopPropagation();
    
    this.isProcessing = true;
    
    // Button deaktivieren und Loading-Zustand anzeigen
    this.setLoadingState(true);

    try {
      // 1. Cart-Daten von Shopify API holen
      console.log('📡 Lade aktuelle Cart-Daten...');
      const cart = await this.fetchCart();
      
      if (!cart || !cart.items || cart.items.length === 0) {
        console.warn('⚠️ Cart ist leer oder konnte nicht geladen werden');
        this.proceedToCheckout();
        return;
      }

      console.log(`📦 Cart geladen mit ${cart.items.length} Items`);

      // 2. Properties aus allen Items extrahieren
      const attributes = this.extractPropertiesAsAttributes(cart.items);
      
      console.log('🔄 Extrahierte Attributes:', attributes);

      // 3. Attributes an Cart senden, falls welche vorhanden
      if (Object.keys(attributes).length > 0) {
        console.log('💾 Sende Attributes an Cart...');
        await this.updateCartAttributes(attributes);
        console.log('✅ Cart Attributes erfolgreich aktualisiert');
      } else {
        console.log('ℹ️ Keine Properties zum Konvertieren gefunden');
      }

      // 4. Weiterleitung zum Checkout
      console.log('🎯 Leite zum Checkout weiter...');
      this.proceedToCheckout();

    } catch (error) {
      console.error('❌ Fehler beim Konvertieren der Properties:', error);
      
      // Bei Fehler trotzdem zum Checkout weiterleiten
      console.log('🔄 Leite trotz Fehler zum Checkout weiter...');
      this.proceedToCheckout();
    } finally {
      this.isProcessing = false;
      this.setLoadingState(false);
    }
  }

  async fetchCart() {
    try {
      const response = await fetch('/cart.js', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('❌ Fehler beim Laden des Carts:', error);
      throw error;
    }
  }

  extractPropertiesAsAttributes(cartItems) {
    const attributes = {};
    let propertyCount = 0;

    cartItems.forEach((item, itemIndex) => {
      console.log(`🔍 Verarbeite Item ${itemIndex + 1}: ${item.product_title || item.title}`);
      
      if (!item.properties || typeof item.properties !== 'object') {
        console.log(`  ℹ️ Keine Properties für Item ${itemIndex + 1}`);
        return;
      }

      // Iterate über alle Properties des Items
      Object.entries(item.properties).forEach(([key, value]) => {
        // Nur Properties die mit _ beginnen (versteckte/interne Properties)
        if (key.startsWith('_') && value && value.trim() !== '') {
          // Erstelle eindeutigen Attribute-Key mit Item-Index
          const attributeKey = `${key}_item_${itemIndex + 1}`;
          attributes[attributeKey] = value;
          propertyCount++;
          
          console.log(`  ✓ Property konvertiert: ${key} -> ${attributeKey} = ${value.substring(0, 100)}${value.length > 100 ? '...' : ''}`);
        } else if (key.startsWith('_')) {
          console.log(`  ⚠️ Property übersprungen (leer): ${key}`);
        }
      });
    });

    console.log(`📊 Insgesamt ${propertyCount} Properties aus ${cartItems.length} Items konvertiert`);
    return attributes;
  }

  async updateCartAttributes(attributes) {
    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          attributes: attributes
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      console.log('📝 Cart Update Response:', result);
      
      return result;
    } catch (error) {
      console.error('❌ Fehler beim Update der Cart Attributes:', error);
      throw error;
    }
  }

  proceedToCheckout() {
    console.log('🎯 Weiterleitung zum Checkout...');
    
    // Versuche die ursprüngliche Checkout-URL zu verwenden
    const checkoutUrl = this.originalCheckoutUrl;
    
    try {
      window.location.href = checkoutUrl;
    } catch (error) {
      console.error('❌ Fehler bei Checkout-Weiterleitung:', error);
      
      // Fallback: Form normal submitten
      const cartForm = document.getElementById('cart');
      if (cartForm) {
        console.log('🔄 Fallback: Form wird normal submitted');
        cartForm.submit();
      }
    }
  }

  setLoadingState(isLoading) {
    if (!this.checkoutButton) return;

    if (isLoading) {
      this.checkoutButton.disabled = true;
      this.checkoutButton.classList.add('loading');
      
      // Speichere ursprünglichen Text
      if (!this.checkoutButton.dataset.originalText) {
        this.checkoutButton.dataset.originalText = this.checkoutButton.textContent;
      }
      
      this.checkoutButton.textContent = 'Verarbeite...';
      
      console.log('⏳ Checkout-Button in Loading-Zustand gesetzt');
    } else {
      this.checkoutButton.disabled = false;
      this.checkoutButton.classList.remove('loading');
      
      // Ursprünglichen Text wiederherstellen
      if (this.checkoutButton.dataset.originalText) {
        this.checkoutButton.textContent = this.checkoutButton.dataset.originalText;
      }
      
      console.log('✅ Checkout-Button Loading-Zustand entfernt');
    }
  }

  // Debug-Funktion für manuelles Testen
  async debugConvertProperties() {
    console.log('🔧 Debug: Manuelle Properties-Konvertierung...');
    
    try {
      const cart = await this.fetchCart();
      console.log('Debug Cart:', cart);
      
      if (cart && cart.items) {
        const attributes = this.extractPropertiesAsAttributes(cart.items);
        console.log('Debug Attributes:', attributes);
        
        if (Object.keys(attributes).length > 0) {
          await this.updateCartAttributes(attributes);
          console.log('✅ Debug: Attributes erfolgreich gesetzt');
        }
      }
    } catch (error) {
      console.error('❌ Debug-Fehler:', error);
    }
  }
}

// Instanz erstellen wenn Seite geladen ist
let cartPropertiesConverter;

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    cartPropertiesConverter = new CartPropertiesConverter();
  });
} else {
  cartPropertiesConverter = new CartPropertiesConverter();
}

// Für Debug-Zwecke global verfügbar machen
window.cartPropertiesConverter = cartPropertiesConverter;

// Auch bei Cart-Updates neu initialisieren
document.addEventListener('cart:updated', () => {
  console.log('🔄 Cart wurde aktualisiert, re-initialisiere Converter...');
  setTimeout(() => {
    if (cartPropertiesConverter) {
      cartPropertiesConverter.setupCheckoutInterceptor();
    }
  }, 100);
});

console.log('✅ Cart Properties to Attributes Converter erfolgreich initialisiert');